import React from 'react';
import { PlanSheet } from '@/app/types/plan';
import { Input } from '@/app/components/ui/input';
import { Settings, PenTool } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface PlanManagerPanelProps {
  plans: PlanSheet[];
  activePlanId?: string;
  onSelectPlan: (id: string) => void;
  onCreatePlan: () => void;
  onDeletePlan: (id: string) => void;
  onUpdatePlan: (id: string, updates: Partial<PlanSheet>) => void;
  onDuplicatePlan?: (id: string) => void;
}

export function PlanManagerPanel({
  plans,
  activePlanId,
  onUpdatePlan,
}: PlanManagerPanelProps) {
  const { t } = useLanguage();
  const activePlan = plans.find(p => p.id === activePlanId);

  if (!activePlan) return null;

  const updateTitleBlock = (key: string, value: string) => {
    onUpdatePlan(activePlan.id, {
      titleBlockData: {
        ...activePlan.titleBlockData,
        [key]: value
      }
    });
  };

  return (
    <div className="flex flex-col h-full bg-white border-l border-gray-200 w-[320px] shadow-sm z-10">
      <div className="flex-1 flex flex-col min-h-0">
        <div className="p-4 bg-gray-50 border-b overflow-y-auto h-full">
          <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-2">
            <Settings className="w-3 h-3" />
            {t.print.settings}: {activePlan.name}
          </h3>
          
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">{t.print.planName}</label>
              <Input 
                value={activePlan.name || ''} 
                onChange={(e) => onUpdatePlan(activePlan.id, { name: e.target.value })}
                className="h-8 bg-white"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">{t.print.format}</label>
                <Select 
                  value={activePlan.format || 'A4'} 
                  onValueChange={(val: any) => onUpdatePlan(activePlan.id, { format: val })}
                >
                  <SelectTrigger className="h-8 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A4">A4</SelectItem>
                    <SelectItem value="A3">A3</SelectItem>
                    <SelectItem value="A2">A2</SelectItem>
                    <SelectItem value="A1">A1</SelectItem>
                    <SelectItem value="A0">A0</SelectItem>
                    <SelectItem value="Custom">{t.print.custom}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">{t.print.orientation}</label>
                <Select 
                  value={activePlan.orientation || 'landscape'} 
                  onValueChange={(val: any) => onUpdatePlan(activePlan.id, { orientation: val })}
                >
                  <SelectTrigger className="h-8 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="landscape">{t.print.landscape}</SelectItem>
                    <SelectItem value="portrait">{t.print.portrait}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">{t.print.scale}</label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">1:</span>
                <Input 
                  type="number"
                  value={activePlan.viewport?.scale ? Math.round(1/activePlan.viewport.scale) : ''} 
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (val > 0) {
                      onUpdatePlan(activePlan.id, { 
                        viewport: { ...activePlan.viewport, scale: 1/val } 
                      });
                    }
                  }}
                  className="h-8 bg-white"
                />
              </div>
            </div>

            {/* Title Block Data Section */}
            <div className="pt-2 border-t border-gray-200 space-y-3">
              <h4 className="text-xs font-bold text-gray-500 uppercase flex items-center gap-2">
                <PenTool className="w-3 h-3" />
                {t.print.titleBlock}
              </h4>
              
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-medium text-gray-500">{t.print.project}</label>
                <textarea 
                  className="w-full text-xs p-2 border rounded-md min-h-[50px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder={t.print.placeholders.project}
                  value={activePlan.titleBlockData?.['projectName'] || ''}
                  onChange={(e) => updateTitleBlock('projectName', e.target.value)}
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] uppercase font-medium text-gray-500">{t.print.client}</label>
                <textarea 
                  className="w-full text-xs p-2 border rounded-md min-h-[50px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder={t.print.placeholders.client}
                  value={activePlan.titleBlockData?.['clientName'] || ''}
                  onChange={(e) => updateTitleBlock('clientName', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-medium text-gray-500">{t.print.projectNumber}</label>
                  <Input 
                    className="h-7 text-xs"
                    value={activePlan.titleBlockData?.['projectNumber'] || ''}
                    onChange={(e) => updateTitleBlock('projectNumber', e.target.value)}
                  />
                </div>
                 <div className="space-y-1">
                  <label className="text-[10px] uppercase font-medium text-gray-500">{t.print.planNumber}</label>
                  <Input 
                    className="h-7 text-xs"
                    value={activePlan.titleBlockData?.['planNumber'] || ''}
                    onChange={(e) => updateTitleBlock('planNumber', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-medium text-gray-500">{t.print.date}</label>
                  <Input 
                    className="h-7 text-xs"
                    type="date"
                    value={activePlan.titleBlockData?.['date'] || new Date().toISOString().split('T')[0]}
                    onChange={(e) => updateTitleBlock('date', e.target.value)}
                  />
                </div>
                 <div className="space-y-1">
                  <label className="text-[10px] uppercase font-medium text-gray-500">{t.print.drawnBy}</label>
                  <Input 
                    className="h-7 text-xs"
                    value={activePlan.titleBlockData?.['drawnBy'] || ''}
                    onChange={(e) => updateTitleBlock('drawnBy', e.target.value)}
                  />
                </div>
              </div>
            </div>
            
            <div className="pt-2 border-t border-gray-200">
              <label className="text-xs font-medium text-gray-700 block mb-2">{t.print.view}</label>
              <div className="flex items-center gap-2 mb-2">
                <input 
                  type="checkbox" 
                  id="chk-crop"
                  checked={activePlan.showCropMarks}
                  onChange={(e) => onUpdatePlan(activePlan.id, { showCropMarks: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="chk-crop" className="text-sm text-gray-600">{t.print.showCropMarks}</label>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <input 
                  type="checkbox" 
                  id="chk-grid"
                  checked={activePlan.showGrid}
                  onChange={(e) => onUpdatePlan(activePlan.id, { showGrid: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="chk-grid" className="text-sm text-gray-600">{t.print.showIsoGrid}</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
